<template>
  <BaseLayout title="Competition settings">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">Competition settings</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="!competitionSettings.homeworks || competitionSettings.homeworks.length === 0" class="alert alert-info text-center">
          No homeworks found to configure for competition. Create homeworks first.
          <button @click="fetchCompetition" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i> Refresh
          </button>
        </div>
        <div v-else>
          <form @submit.prevent="handleSaveSettings">
            <!-- Pager Controls -->
            <div class="d-flex justify-content-between align-items-center mb-3" v-if="totalPages > 0">
              <button
                type="button"
                :class="['btn', hasPrev ? 'btn-primary' : 'btn-outline-secondary']"
                @click="prevPage"
                :disabled="!hasPrev"
                :title="hasPrev ? 'Previous homework' : 'No previous homework'"
                aria-label="Previous homework"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
              <div>
                <span class="me-2">Page</span>
                <span><b>{{ currentIndex + 1 }}</b> / {{ totalPages }}</span>
              </div>
              <button
                type="button"
                :class="['btn', hasNext ? 'btn-primary' : 'btn-outline-secondary']"
                @click="nextPage"
                :disabled="!hasNext"
                :title="hasNext ? 'Next homework' : 'No next homework'"
                aria-label="Next homework"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>

            <!-- Single Homework Card -->
            <div v-if="currentHomework" class="card mb-3">
              <div class="card-header d-flex justify-content-between align-items-center">
                <span>
                  <b>{{ currentHomework.title }}</b> (release date: {{ formatDate(currentHomework.release_date) }})
                  <span v-if="new Date(currentHomework.release_date) > new Date()" class="badge bg-warning ms-2" v-tooltip="'Homeworks that are not yet released are not included in the competition results.'">
                    Not released <i class="bi bi-question-circle me-1"></i>
                  </span>
                  <span v-if="!currentHomework.in_competition" class="badge bg-danger ms-2">Not in competition</span>
                </span>
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" :id="'in_competition-' + currentHomework.id" v-model="currentHomework.in_competition">
                  <label class="form-check-label" :for="'in_competition-' + currentHomework.id">In Competition</label>
                </div>
              </div>
              <div class="card-body">
                <!-- Deadlines -->
                <h5>Deadlines</h5>
                <div v-if="currentHomework.deadlines && currentHomework.deadlines.length">
                  <table class="table table-bordered">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th style="width: 140px;">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(deadline, dIdx) in currentHomework.deadlines" :key="deadline.id">
                        <td>{{ formatDateTime(deadline.date) }}</td>
                        <td>{{ getEvaluationTypeLabel(deadline.evaluation_type) }}</td>
                        <td>
                          <input type="number" class="form-control" min="0" v-model.number="currentHomework.deadlines[dIdx].points" @input="validatePoints(currentHomework, dIdx)" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="alert alert-info">No deadlines for this homework.</div>

                <!-- Bonuses -->
                <div class="d-flex justify-content-between align-items-center mt-4">
                    <h5>Bonuses</h5>
                    <small class="text-muted">(Bonuses are included in competition only when homework is included)</small>
                  <button type="button" class="btn btn-sm btn-success" data-bs-toggle="modal" data-bs-target="#createCompetitionBonusModalForm" :disabled="!currentHomework">
                    <i class="bi bi-plus-lg"></i> Add Bonus
                  </button>
                </div>
                <div v-if="currentHomework.bonuses && currentHomework.bonuses.length">
                  <table class="table table-bordered">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Subtask ID</th>
                        <th>Prefix</th>
                        <th>Metric</th>
                        <th>Top students</th>
                        <th style="width: 140px;">Points</th>
                        <th style="width: 80px;">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(bonus, bIdx) in currentHomework.bonuses" :key="bonus.id">
                        <td>
                          <input type="text" class="form-control" v-model="currentHomework.bonuses[bIdx].title" @input="validateBonus(bIdx)" required />
                        </td>
                        <td>{{ bonus.subtask_id }}</td>
                        <td>
                          <input type="text" class="form-control" v-model="currentHomework.bonuses[bIdx].prefix" @input="validateBonus(bIdx)" required />
                        </td>
                        <td>
                          <select class="form-select" v-model="currentHomework.bonuses[bIdx].metric" @change="validateBonus(bIdx)" required>
                            <option v-for="m in metricValues" :key="m" :value="m">{{ m }}</option>
                          </select>
                        </td>
                        <td>
                          <input type="number" min="1" class="form-control" v-model.number="currentHomework.bonuses[bIdx].top_k" @input="validateBonus(bIdx)" required />
                        </td>
                        <td>
                          <input type="number" min="0" class="form-control" v-model.number="currentHomework.bonuses[bIdx].points" @input="validateBonus(bIdx)" required />
                        </td>
                        <td>
                          <button type="button" class="btn btn-sm btn-outline-danger" @click="handleDeleteBonus(currentHomework.id, bonus.id)" title="Delete bonus">
                            <i class="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="alert alert-info">No bonuses for this homework.</div>
              </div>
            </div>
            <button type="submit" class="btn btn-primary mt-3" :disabled="saving">Save Settings</button>
          </form>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link v-if="isTeacher" to="/competition" class="btn btn-primary mb-2">
          <i class="bi bi-arrow-left"></i> Back to competition
        </router-link>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal"/>
    <!-- Modal: Create Competition Bonus -->
    <CreateCompetitionBonusModalForm v-if="currentHomework"
      :homework="currentHomework"
      @submitForm="handleCreateBonus"
    />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import CompetitionController from '@/controllers/CompetitionController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import CompetitionSetting, { CompetitionMetric } from '@/models/competition';
import CreateCompetitionBonusModalForm from '@/components/CreateCompetitionBonusModalForm.vue';
import Deadline from '@/models/deadline';
import { mapGetters } from 'vuex';

export default {
  name: 'CompetitionView',
  components: {
    BaseLayout,
  StemErrorModal,
  CreateCompetitionBonusModalForm
  },
  data() {
    return {
      competitionSettings: [],
      localCompetitionSettings: new CompetitionSetting({}),
      loading: true,
      saving: false,
      currentIndex: 0
    };
  },
  computed: {
    ...mapGetters(['isTeacher']),
    currentHomework() {
      const list = this.localCompetitionSettings?.homeworks || [];
      if (!list.length) return null;
      return list[Math.min(Math.max(this.currentIndex, 0), list.length - 1)];
    },
    totalPages() {
      return (this.localCompetitionSettings?.homeworks || []).length;
    },
    hasPrev() {
      return this.currentIndex > 0;
    },
    hasNext() {
      return this.currentIndex < this.totalPages - 1;
    },
    metricValues() {
      return CompetitionMetric.values();
    }
  },
  async created() {
    try {
      // Check if the user is a teacher
      if (!this.isTeacher) {
        this.$router.push('/competition');
        return;
      }
      this.competitionSettings = await CompetitionController.fetchCompetitionSetting();
      this.localCompetitionSettings = new CompetitionSetting(JSON.parse(JSON.stringify(this.competitionSettings)));
      this.orderHomeworksAndDeadlines();
  this.currentIndex = 0;
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatDate(date) {
      return Deadline.formatDate(date);
    },
    formatDateTime(date) {
      return Deadline.formatDateTime(date);
    },
    validatePoints(homework, dIdx) {
      const points = homework.deadlines[dIdx].points;
      if (!CompetitionSetting.validatePoints(points)) {
        homework.deadlines[dIdx].points = 0;
      }
    },
    orderHomeworksAndDeadlines() {
      // Sort homeworks by release_date
      if (this.localCompetitionSettings.homeworks) {
        this.localCompetitionSettings.homeworks.sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
        // Sort deadlines by date for each homework
        this.localCompetitionSettings.homeworks.forEach(hw => {
          if (hw.deadlines) {
            hw.deadlines.sort((a, b) => new Date(a.date) - new Date(b.date));
          }
        });
      }
    },
    async fetchCompetition() {
      this.loading = true;
      try {
        this.competitionSettings = await CompetitionController.fetchCompetitionSetting();
        this.localCompetitionSettings = new CompetitionSetting(JSON.parse(JSON.stringify(this.competitionSettings)));
        this.orderHomeworksAndDeadlines();
        this.currentIndex = 0;
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async handleSaveSettings() {
      this.saving = true;
      try {
        await CompetitionController.saveCompetitionSetting(this.localCompetitionSettings);
        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Competition settings saved successfully.',
          type: 'success',
        });
        this.fetchCompetition();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.saving = false;
      }
    },
    getEvaluationTypeLabel(type) {
      return Deadline.getEvaluationTypeLabel(type);
    },
    async handleCreateBonus(bonusPayload) {
      if (!this.currentHomework) return;
      try {
        this.saving = true;
        const updated = await CompetitionController.createCompetitionBonus(this.currentHomework.id, bonusPayload);
        this.competitionSettings = updated;
        this.localCompetitionSettings = new CompetitionSetting(JSON.parse(JSON.stringify(updated)));
        this.orderHomeworksAndDeadlines();
        this.$store.dispatch('triggerAlert', { title: 'Success', message: 'Bonus created successfully.', type: 'success' });
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.saving = false;
      }
    },
    nextPage() {
      if (this.currentIndex < this.totalPages - 1) this.currentIndex++;
    },
    prevPage() {
      if (this.currentIndex > 0) this.currentIndex--;
    },
    validateBonus(bIdx) {
      const bonus = this.currentHomework?.bonuses?.[bIdx];
      if (!bonus) return;
      // points non-negative integer
      if (!Number.isInteger(bonus.points) || bonus.points < 0) bonus.points = 0;
      // top_k integer >= 1
      if (!Number.isInteger(bonus.top_k) || bonus.top_k < 1) bonus.top_k = 1;
      // metric must be one of allowed
      if (!this.metricValues.includes(bonus.metric)) bonus.metric = CompetitionMetric.ASC;
    },
    async handleDeleteBonus(homeworkId, bonusId) {
      try {
        this.saving = true;
        const updated = await CompetitionController.deleteCompetitionBonus(homeworkId, bonusId);
        this.competitionSettings = updated;
        this.localCompetitionSettings = new CompetitionSetting(JSON.parse(JSON.stringify(updated)));
        this.orderHomeworksAndDeadlines();
        if (this.currentIndex >= this.totalPages) this.currentIndex = Math.max(0, this.totalPages - 1);
        this.$store.dispatch('triggerAlert', { title: 'Deleted', message: 'Bonus deleted.', type: 'success' });
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
